import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, XCircle, Eye, EyeOff, Home, MapPin, ExternalLink, FileSearch, Star, MoreVertical,
  Send, MessageSquare, RotateCcw,
} from 'lucide-react';
import { BulkActionBar } from './BulkActionBar';
import { logAdminAction } from '@/lib/auditLog';
import { PackageQuotaBadge } from './PackageQuotaBadge';

const statusConfig: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  pending_review: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
};

const propStatusConfig: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  draft: 'bg-gray-100 text-gray-700',
  sold: 'bg-blue-100 text-blue-800',
  rented: 'bg-purple-100 text-purple-800',
};

const actionConfig: Record<string, { label: string; dot: string; icon: any }> = {
  feedback_sent: { label: 'Feedback sent', dot: 'bg-amber-500', icon: MessageSquare },
  resubmitted: { label: 'Resubmitted by owner', dot: 'bg-blue-500', icon: RotateCcw },
  approved: { label: 'Approved', dot: 'bg-green-500', icon: CheckCircle2 },
  rejected: { label: 'Rejected', dot: 'bg-red-500', icon: XCircle },
};

const getName = (val: any) => typeof val === 'object' ? (val?.en || '') : (val || '');

export function PropertyApprovals() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedProp, setSelectedProp] = useState<any>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [markFeatured, setMarkFeatured] = useState(false);
  const [filterTab, setFilterTab] = useState('pending_review');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const toggleSelected = (id: string) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSelected = () => setSelectedIds(new Set());

  const { data: properties, isLoading } = useQuery({
    queryKey: ['properties-for-approval'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('properties')
        .select('*, property_images(url, is_primary)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // History for currently-open property
  const { data: history } = useQuery({
    queryKey: ['property-review-history', selectedProp?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('property_review_history' as any)
        .select('*')
        .eq('property_id', selectedProp!.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!selectedProp,
  });

  const writeHistory = async (propertyId: string, action: string, notes: string) => {
    if (!user) return;
    await supabase.from('property_review_history' as any).insert({
      property_id: propertyId,
      reviewer_id: user.id,
      action,
      notes: notes || null,
    });
  };

  // Merge pending edits into the live row + replace images, then clear pending fields
  const mergePendingChanges = async (propertyId: string, pending: any) => {
    if (!pending) return;
    const liveColumns = [
      'title', 'description', 'property_type', 'listing_type', 'price', 'currency',
      'city', 'sub_city', 'woreda', 'address', 'area_sqm', 'bedrooms', 'bathrooms',
      'floor', 'total_floors', 'year_built', 'features',
      'contact_person', 'contact_phone', 'contact_email',
    ];
    const liveUpdate: any = {};
    liveColumns.forEach(col => { if (pending[col] !== undefined) liveUpdate[col] = pending[col]; });
    if (Object.keys(liveUpdate).length > 0) {
      await supabase.from('properties').update(liveUpdate).eq('id', propertyId);
    }
    if (Array.isArray(pending._images)) {
      await supabase.from('property_images').delete().eq('property_id', propertyId);
      if (pending._images.length > 0) {
        await supabase.from('property_images').insert(
          pending._images.map((img: any, i: number) => ({
            property_id: propertyId,
            url: img.url,
            is_primary: !!img.is_primary,
            sort_order: i,
          }))
        );
      }
    }
  };

  const reviewMutation = useMutation({
    mutationFn: async ({ propId, status, notes, featured, property }: { propId: string; status: string; notes: string; featured?: boolean; property: any }) => {
      const hasPending = !!property.has_pending_changes;
      const isApprovedWithEdits = property.approval_status === 'approved' && hasPending;

      if (isApprovedWithEdits && status === 'approved') {
        await mergePendingChanges(propId, property.pending_changes);
        const update: any = { reviewer_notes: notes, has_pending_changes: false, pending_changes: null };
        if (featured) update.is_featured = true;
        const { error } = await supabase.from('properties').update(update).eq('id', propId);
        if (error) throw error;
      } else if (isApprovedWithEdits && status === 'rejected') {
        const { error } = await supabase
          .from('properties')
          .update({ reviewer_notes: notes, has_pending_changes: false, pending_changes: null })
          .eq('id', propId);
        if (error) throw error;
      } else {
        const update: any = { approval_status: status, reviewer_notes: notes };
        if (status === 'approved') {
          update.status = 'active';
          if (featured) update.is_featured = true;
        }
        const { error } = await supabase.from('properties').update(update).eq('id', propId);
        if (error) throw error;
      }

      await writeHistory(propId, status, notes);

      if (property.user_id) {
        const propTitle = getName(property.title);
        const truncated = notes ? (notes.length > 200 ? notes.slice(0, 200) + '…' : notes) : '';
        const title = isApprovedWithEdits
          ? (status === 'approved' ? `Edits approved: ${propTitle}` : `Edits rejected: ${propTitle}`)
          : (status === 'approved' ? `Property approved: ${propTitle}` : `Property rejected: ${propTitle}`);
        const message = truncated || (
          isApprovedWithEdits
            ? (status === 'approved' ? 'Your edits have been approved and are now live.' : 'Your edits were rejected. The previous live version remains visible.')
            : (status === 'approved' ? 'Your property has been approved and is now live.' : 'Your property was rejected. Please review the feedback.')
        );
        await supabase.from('notifications').insert({
          user_id: property.user_id,
          type: 'property_reviewed',
          title,
          message,
          metadata: { property_id: propId, status, notes, was_pending_edits: isApprovedWithEdits },
        });
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['properties-for-approval'] });
      queryClient.invalidateQueries({ queryKey: ['property-review-history'] });
      toast.success(vars.status === 'approved' ? 'Approved! Owner notified.' : 'Rejected. Owner notified.');
      setSelectedProp(null);
      setReviewNotes('');
      setMarkFeatured(false);
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Send feedback WITHOUT changing approval status
  const sendFeedbackMutation = useMutation({
    mutationFn: async ({ propId, notes, property }: { propId: string; notes: string; property: any }) => {
      const { error } = await supabase.from('properties').update({ reviewer_notes: notes }).eq('id', propId);
      if (error) throw error;

      await writeHistory(propId, 'feedback_sent', notes);

      if (property.user_id) {
        const propTitle = getName(property.title);
        const truncated = notes.length > 200 ? notes.slice(0, 200) + '…' : notes;
        await supabase.from('notifications').insert({
          user_id: property.user_id,
          type: 'property_review_feedback',
          title: `QC sent feedback on: ${propTitle}`,
          message: truncated,
          metadata: { property_id: propId, action: 'feedback_sent', notes },
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties-for-approval'] });
      queryClient.invalidateQueries({ queryKey: ['property-review-history'] });
      toast.success('Feedback sent to owner.');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const quickActionMutation = useMutation({
    mutationFn: async ({ propId, updates }: { propId: string; updates: Record<string, any> }) => {
      const { error } = await supabase.from('properties').update(updates as any).eq('id', propId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties-for-approval'] });
      toast.success('Property updated');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const bulkMutation = useMutation({
    mutationFn: async ({ ids, action }: { ids: string[]; action: 'approve' | 'reject' | 'feature' | 'unfeature' }) => {
      const update: any = action === 'approve' ? { approval_status: 'approved', status: 'active' }
        : action === 'reject' ? { approval_status: 'rejected', status: 'draft' }
        : action === 'feature' ? { is_featured: true }
        : { is_featured: false };
      const { error } = await supabase.from('properties').update(update).in('id', ids);
      if (error) throw error;
      await logAdminAction({
        action: action === 'approve' ? 'bulk_approve' : action === 'reject' ? 'bulk_reject' : action === 'feature' ? 'bulk_feature' : 'bulk_unfeature',
        entity_type: 'property',
        metadata: { ids, count: ids.length },
      });
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['properties-for-approval'] });
      toast.success(`${vars.ids.length} properties ${vars.action}d`);
      clearSelected();
    },
    onError: (err: any) => toast.error(err.message),
  });

  const filtered = properties?.filter((p: any) => {
    if (filterTab === 'all') return true;
    if (filterTab === 'pending_review') return p.approval_status === 'pending_review' || p.has_pending_changes;
    return p.approval_status === filterTab;
  }) || [];

  const pendingCount = properties?.filter((p: any) => p.approval_status === 'pending_review' || p.has_pending_changes).length || 0;

  if (isLoading) return <div className="space-y-4">{[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold">Property Management</h2>
        {pendingCount > 0 && <Badge className="bg-yellow-500 text-white">{pendingCount} Pending</Badge>}
      </div>

      <Tabs value={filterTab} onValueChange={setFilterTab}>
        <TabsList>
          <TabsTrigger value="all">All ({properties?.length || 0})</TabsTrigger>
          <TabsTrigger value="pending_review">Pending ({pendingCount})</TabsTrigger>
          <TabsTrigger value="approved">Approved ({properties?.filter((p: any) => p.approval_status === 'approved').length || 0})</TabsTrigger>
          <TabsTrigger value="rejected">Rejected ({properties?.filter((p: any) => p.approval_status === 'rejected').length || 0})</TabsTrigger>
        </TabsList>
      </Tabs>

      {!filtered.length ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <FileSearch className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No properties in this category</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          <BulkActionBar
            selectedCount={selectedIds.size}
            onClear={clearSelected}
            onApprove={() => bulkMutation.mutate({ ids: Array.from(selectedIds), action: 'approve' })}
            onReject={() => bulkMutation.mutate({ ids: Array.from(selectedIds), action: 'reject' })}
            onFeature={() => bulkMutation.mutate({ ids: Array.from(selectedIds), action: 'feature' })}
            onUnfeature={() => bulkMutation.mutate({ ids: Array.from(selectedIds), action: 'unfeature' })}
            pending={bulkMutation.isPending}
          />
          {filtered.map((prop: any) => {
            const title = getName(prop.title);
            const cfg = statusConfig[prop.approval_status] || statusConfig.draft;
            const img = prop.property_images?.find((i: any) => i.is_primary)?.url || prop.property_images?.[0]?.url;
            return (
              <Card key={prop.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Checkbox
                      checked={selectedIds.has(prop.id)}
                      onCheckedChange={() => toggleSelected(prop.id)}
                      aria-label="Select property"
                    />
                    <div className="h-14 w-14 rounded-lg bg-muted overflow-hidden shrink-0">
                      {img ? <img src={img} alt={title} className="h-full w-full object-cover" /> : <Home className="h-6 w-6 text-muted-foreground m-auto mt-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold truncate">{title}</span>
                        {prop.is_featured && <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />}
                      </div>
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {prop.city}{prop.sub_city ? `, ${prop.sub_city}` : ''} • {prop.property_type} • {prop.listing_type}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {prop.price?.toLocaleString()} {prop.currency} • {prop.bedrooms || 0} bed • {prop.area_sqm || 0} sqm
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={cfg.color}>{cfg.label}</Badge>
                      {prop.approval_status === 'approved' && (
                        <Badge className={propStatusConfig[prop.status] || propStatusConfig.draft} variant="outline">
                          {prop.status}
                        </Badge>
                      )}
                      {prop.has_pending_changes && (
                        <Badge variant="outline" className="border-blue-400 text-blue-700 bg-blue-50 text-[10px]">
                          Pending Edits
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Link to={`/property/${(prop as any).slug || prop.id}`} target="_blank"><Button variant="ghost" size="sm"><ExternalLink className="h-4 w-4" /></Button></Link>
                      <Button variant="outline" size="sm" onClick={() => {
                        setSelectedProp(prop);
                        setReviewNotes(prop.reviewer_notes || '');
                        setMarkFeatured(prop.is_featured || false);
                      }}>
                        <Eye className="h-4 w-4 mr-1" /> Review
                      </Button>
                      {prop.approval_status === 'approved' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {prop.status === 'active' ? (
                              <DropdownMenuItem onClick={() => quickActionMutation.mutate({ propId: prop.id, updates: { status: 'draft' } })}>
                                <EyeOff className="h-4 w-4 mr-2" /> Hide from Site
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => quickActionMutation.mutate({ propId: prop.id, updates: { status: 'active' } })}>
                                <Eye className="h-4 w-4 mr-2" /> Show on Site
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => quickActionMutation.mutate({ propId: prop.id, updates: { is_featured: !prop.is_featured } })}>
                              <Star className="h-4 w-4 mr-2" /> {prop.is_featured ? 'Remove Featured' : 'Mark Featured'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => quickActionMutation.mutate({ propId: prop.id, updates: { approval_status: 'rejected', status: 'draft' } })}>
                              <XCircle className="h-4 w-4 mr-2" /> Revoke Approval
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={!!selectedProp} onOpenChange={() => setSelectedProp(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
            <DialogTitle>Review: {selectedProp && getName(selectedProp.title)}</DialogTitle>
          </DialogHeader>
          {selectedProp && (
            <>
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                <div className="text-sm space-y-1.5 bg-muted/50 rounded-lg p-3">
                  <div><strong>Type:</strong> {selectedProp.property_type} ({selectedProp.listing_type})</div>
                  <div><strong>Location:</strong> {selectedProp.city}{selectedProp.sub_city ? `, ${selectedProp.sub_city}` : ''}{selectedProp.woreda ? `, ${selectedProp.woreda}` : ''}</div>
                  <div><strong>Price:</strong> {selectedProp.price?.toLocaleString()} {selectedProp.currency}</div>
                  <div><strong>Area:</strong> {selectedProp.area_sqm || 'N/A'} sqm • <strong>Bedrooms:</strong> {selectedProp.bedrooms || 'N/A'} • <strong>Bathrooms:</strong> {selectedProp.bathrooms || 'N/A'}</div>
                  {selectedProp.contact_person && <div><strong>Contact:</strong> {selectedProp.contact_person} {selectedProp.contact_phone && `• ${selectedProp.contact_phone}`}</div>}
                  <div><strong>Status:</strong> {selectedProp.status} • <strong>Approval:</strong> {selectedProp.approval_status}</div>
                  <div className="pt-1">
                    <PackageQuotaBadge userId={selectedProp.user_id} kind="listing" approvalStatus={selectedProp.approval_status} />
                  </div>
                </div>

                {selectedProp.property_images?.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {selectedProp.property_images.map((img: any, i: number) => (
                      <img key={i} src={img.url} alt="" className="h-16 w-20 rounded object-cover shrink-0" />
                    ))}
                  </div>
                )}

                {/* Pending edits diff */}
                {selectedProp.has_pending_changes && selectedProp.pending_changes && (() => {
                  const pc = selectedProp.pending_changes;
                  const diffFields = ['title', 'description', 'price', 'currency', 'city', 'sub_city', 'woreda', 'address', 'property_type', 'listing_type', 'area_sqm', 'bedrooms', 'bathrooms', 'floor', 'total_floors', 'year_built'];
                  const renderVal = (v: any) => {
                    if (v === null || v === undefined || v === '') return <em className="text-muted-foreground">empty</em>;
                    if (typeof v === 'object') return v.en || JSON.stringify(v);
                    return String(v);
                  };
                  const changes = diffFields
                    .map(f => ({ field: f, oldVal: (selectedProp as any)[f], newVal: pc[f] }))
                    .filter(c => c.newVal !== undefined && JSON.stringify(c.oldVal) !== JSON.stringify(c.newVal));
                  return (
                    <div className="space-y-2 rounded-lg border-2 border-blue-300 bg-blue-50/50 p-3">
                      <div className="text-sm font-semibold flex items-center gap-1.5 text-blue-900">
                        <FileSearch className="h-4 w-4" /> Pending Edits Submitted by Owner
                      </div>
                      {changes.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No field-level changes detected, but images may have been updated.</p>
                      ) : (
                        <div className="space-y-1.5 text-xs">
                          {changes.map(c => (
                            <div key={c.field} className="grid grid-cols-[1fr_2fr] gap-2 items-start border-b border-blue-200/60 pb-1 last:border-0">
                              <span className="font-medium capitalize text-blue-900">{c.field.replace(/_/g, ' ')}</span>
                              <div className="space-y-0.5">
                                <div className="text-red-700 line-through opacity-70">{renderVal(c.oldVal)}</div>
                                <div className="text-green-700 font-medium">{renderVal(c.newVal)}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {Array.isArray(pc._images) && (
                        <div className="text-[11px] text-blue-800">
                          <strong>Updated photos ({pc._images.length}):</strong>
                          <div className="mt-1 flex gap-1 overflow-x-auto">
                            {pc._images.slice(0, 8).map((img: any, i: number) => (
                              <img key={i} src={img.url} alt="" className="h-12 w-16 rounded object-cover shrink-0 border-2 border-blue-300" />
                            ))}
                          </div>
                        </div>
                      )}
                      <p className="text-[11px] italic text-blue-800">
                        Approving will apply these edits to the live listing. Rejecting will discard them and keep the current live version.
                      </p>
                    </div>
                  );
                })()}

                <div className="space-y-1.5">
                  <Label htmlFor="prop-review-notes" className="text-sm font-medium">Review Notes (visible to owner)</Label>
                  <Textarea
                    id="prop-review-notes"
                    placeholder="Write feedback the owner will see..."
                    value={reviewNotes}
                    onChange={e => setReviewNotes(e.target.value)}
                    rows={4}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Switch checked={markFeatured} onCheckedChange={setMarkFeatured} id="featured-toggle" />
                  <Label htmlFor="featured-toggle" className="flex items-center gap-1.5">
                    <Star className="h-3.5 w-3.5" /> Mark as Featured Property
                  </Label>
                </div>

                {/* Review history timeline */}
                {history && history.length > 0 && (
                  <div className="space-y-2 pt-2 border-t">
                    <div className="text-sm font-semibold flex items-center gap-1.5">
                      <MessageSquare className="h-4 w-4" /> Review History ({history.length})
                    </div>
                    <div className="space-y-2.5">
                      {history.map((h: any) => {
                        const cfg = actionConfig[h.action] || actionConfig.feedback_sent;
                        const Icon = cfg.icon;
                        return (
                          <div key={h.id} className="flex gap-3 text-xs">
                            <div className="flex flex-col items-center">
                              <div className={`h-2.5 w-2.5 rounded-full ${cfg.dot} mt-1.5`} />
                            </div>
                            <div className="flex-1 bg-muted/40 rounded p-2">
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-medium flex items-center gap-1">
                                  <Icon className="h-3 w-3" /> {cfg.label}
                                </span>
                                <span className="text-muted-foreground">{new Date(h.created_at).toLocaleString()}</span>
                              </div>
                              {h.notes && <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{h.notes}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="gap-2 px-6 py-4 border-t shrink-0 bg-background flex-wrap">
                {(selectedProp.approval_status === 'pending_review' || selectedProp.has_pending_changes) ? (
                  <>
                    <Button
                      variant="destructive"
                      onClick={() => {
                        if (!reviewNotes.trim()) {
                          toast.error('Please provide review notes when rejecting.');
                          return;
                        }
                        reviewMutation.mutate({ propId: selectedProp.id, status: 'rejected', notes: reviewNotes, property: selectedProp });
                      }}
                      disabled={reviewMutation.isPending || sendFeedbackMutation.isPending}
                    >
                      <XCircle className="h-4 w-4 mr-1" /> {selectedProp.has_pending_changes && selectedProp.approval_status === 'approved' ? 'Reject Edits' : 'Reject'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        if (!reviewNotes.trim()) {
                          toast.error('Please write feedback before sending.');
                          return;
                        }
                        sendFeedbackMutation.mutate({ propId: selectedProp.id, notes: reviewNotes, property: selectedProp });
                      }}
                      disabled={sendFeedbackMutation.isPending || reviewMutation.isPending}
                    >
                      <Send className="h-4 w-4 mr-1" /> Send Feedback
                    </Button>
                    <Button
                      onClick={() => reviewMutation.mutate({ propId: selectedProp.id, status: 'approved', notes: reviewNotes, featured: markFeatured, property: selectedProp })}
                      disabled={reviewMutation.isPending || sendFeedbackMutation.isPending}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" /> {selectedProp.has_pending_changes && selectedProp.approval_status === 'approved' ? 'Approve Edits' : 'Approve'}
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => {
                    quickActionMutation.mutate({ propId: selectedProp.id, updates: { reviewer_notes: reviewNotes, is_featured: markFeatured } });
                    setSelectedProp(null);
                  }}>
                    Save Changes
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
